import AnimationClip from '../AnimationClip';
import { Tween } from '@tweenjs/tween.js';
import GameObject from '../../../GameObject/GameObject';
import ElementComponent from '../../../Component/UI/Element';
import ColorFilter from '../../../Component/ColorFilter';
import { AwaitIterators } from '../AnimationHelper';
import TransformComponent from '../../../Component/Transform';
import Point from '../../../Utility/Point';
import SpriteComponent from 'Component/Render/Sprite';
import Sprite from 'Prototype/Sprite';
import UIImageComponent from 'Component/UI/Image';
import ShadowComponent from 'Component/UI/ShadowComponent';

const shadowTexturesRecord: Record<string, string> = {
    audiomusic_0: 'square_push',
    audiomusic_1: 'square_push',
    audiosfx_0: 'square_push',
    audiosfx_1: 'square_push',
    audiomusic_0_2: 'square_push_2',
    audiomusic_1_2: 'square_push_2',
    audiosfx_0_2: 'square_push_2',
    audiosfx_1_2: 'square_push_2',
    button_round_settings: 'square_push',
    button_blue_friend: 'friends_send_push',
    button_green_friends: 'friends_send_push',
    button_grey_friends: 'friends_send_push',
    button_medium_blue: 'standart_push',
    button_medium_green: 'standart_push',
    button_medium_purple: 'standart_push',
    button_medium_red: 'standart_push',
    button_medium_blue_2: 'small_push',
    friend_helper_button: 'small_push',
    button_round_play: 'play_push',
    button_shop_coins: 'no_padding_push',
    button_small_blue: 'small_push',
    button_small_red: 'small_push',
    button_arrow: 'push_arrow',
    button_long: 'button_long_push',
    no_padding_button: 'no_padding_push',
    daily_collect: 'daily_button_push',
    luckyturnips_button_front: 'standart_push',
    pet_orange_button: 'standart_push',
    button_round_undo: 'square_push',
    mission_collect: 'xxs_push',
    button_medium_orange: 'standart_push',
    blue_button: 'bluebutton_push',
    green_button: 'greenbutton_push',
    fb_connect_btn_medium: 'connect_medium_push',
    google_play_connect_btn: 'connect_medium_push',
    google_play_disconnect: 'google_play_disconnect_push',
};

type Meta = {
    scale: Point;
    forceNoShadow?: boolean;
    [key: string]: any;
};

export class ButtonPress extends AnimationClip {
    public *execute(gameObject: GameObject, meta?: Meta): IterableIterator<number> {
        gameObject.addComponent(ShadowComponent);
        const shadowComponent = gameObject.getComponent(ShadowComponent);
        const imageComponent = gameObject.getComponent(UIImageComponent);
        let sprite;

        if (gameObject.container instanceof PIXI.Sprite) {
            sprite = gameObject.container;
        } else if (imageComponent) {
            sprite = imageComponent.UIComponent.imageSprite;
        } else {
            // no sprite, no animation
            console.warn('ButtonPress animation not played because there is no sprite');
            return;
        }

        const asset = sprite.texture.textureCacheIds[0];

        if (!meta.forceNoShadow && ((asset && shadowTexturesRecord[asset]) || shadowComponent.shadow)) {
            if (!shadowComponent.shadow) {
                shadowComponent.shadow = GameObject.Instantiate(Sprite, {
                    texture: shadowTexturesRecord[asset],
                    alpha: 0,
                });
            }
            gameObject.addChild(shadowComponent.shadow);

            if (imageComponent) {
                const position = new PIXI.Point(sprite.texture.width / 2, sprite.texture.height / 2);
                gameObject.container.toLocal(position, sprite, shadowComponent.shadow.container.position);
                shadowComponent.shadow.getComponent(SpriteComponent).anchor.set(0.5);
            } else {
                shadowComponent.shadow.getComponent(SpriteComponent).anchor.copy(sprite.anchor);
            }

            yield* this.shadowAnimation(shadowComponent.shadow);
        } else {
            yield* this.colorScaleAnimation(gameObject, meta);
        }
    }

    protected getScaleTween(gameObject: GameObject, scale: Point, percent: number = 0.95): Tween {
        const component: TransformComponent | ElementComponent = gameObject.hasComponent(ElementComponent)
            ? gameObject.getComponent(ElementComponent)
            : gameObject.getComponent(TransformComponent);

        const scaleTween: Tween = this.getTween(component.scale);
        scaleTween.to({ x: scale.x * percent, y: scale.y * percent }, 100);
        return scaleTween;
    }

    private *shadowAnimation(shadow: Sprite) {
        const shadowSprite = shadow.getComponent(SpriteComponent);
        const tween = this.getTween(shadowSprite).to({ alpha: 1 }, 100);
        yield* this.startTween(tween);
    }

    private *colorScaleAnimation(gameObject: GameObject, meta?: Meta) {
        let colorFilter: ColorFilter = gameObject.getComponent(ColorFilter);
        if (colorFilter) {
            colorFilter.brightness = 1;
        } else {
            colorFilter = gameObject.addComponent<ColorFilter>(ColorFilter, {
                brightness: 1,
            });
        }

        const colorTween: Tween = this.getTween(colorFilter);
        colorTween.to({ brightness: 1.2 }, 100);

        const scale: Point = new Point(meta.scale.x, meta.scale.y);
        const scaleTween = this.getScaleTween(gameObject, scale);

        yield* AwaitIterators([this.startTween(scaleTween), this.startTween(colorTween)]);
    }
}
